import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc, setDoc, deleteDoc, addDoc, serverTimestamp, updateDoc, arrayUnion, onSnapshot } from 'firebase/firestore';
import { auth, db } from './firebase-config';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';
import './ActiveApplications.css';

const TABS = [
  { key: 'applied', label: 'Applied' },
  { key: 'saved', label: 'Saved' },
  { key: 'passed', label: 'Passed' },
];

const ActiveApplications = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isEmployer, setIsEmployer] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState('applied');
  const [declinedJobs, setDeclinedJobs] = useState([]);
  const [savedJobs, setSavedJobs] = useState([]);
  const [isApplying, setIsApplying] = useState(false);
  const [showAppSent, setShowAppSent] = useState(false);
  const [appSentFading, setAppSentFading] = useState(false);
  const [modalFading, setModalFading] = useState(false);
  const [appliedJobIds, setAppliedJobIds] = useState([]);
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [pendingExternalJob, setPendingExternalJob] = useState(null);
  const [showExternalApplyConfirm, setShowExternalApplyConfirm] = useState(false);
  const [pendingInternalJob, setPendingInternalJob] = useState(null);
  const [showInternalApplyConfirm, setShowInternalApplyConfirm] = useState(false);

  useEffect(() => {
    const fetchApplications = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate('/login');
        return;
      }
      setIsLoading(true);
      try {
        // Fetch applications
        const applicationsQuery = query(
          collection(db, 'applications'),
          where('employeeId', '==', user.uid)
        );
        const applicationsSnapshot = await getDocs(applicationsQuery);
        const applicationsList = await Promise.all(
          applicationsSnapshot.docs.map(async (docSnap) => {
            const applicationData = docSnap.data();
            
            if (applicationData.isExternal) {
              return {
                id: docSnap.id,
                ...applicationData,
                jobTitle: applicationData.jobTitle || 'Unknown Job',
                company: applicationData.company || 'Unknown Company',
                jobDetails: {
                  title: applicationData.jobTitle,
                  company: applicationData.company,
                  description: 'External job posting',
                  requirements: 'See external listing for requirements'
                },
              };
            }
            
            const jobDoc = await getDoc(doc(db, 'jobs', applicationData.jobId));
            const jobData = jobDoc.data();
            return {
              id: docSnap.id,
              ...applicationData,
              jobTitle: jobData?.title || applicationData.jobTitle || 'Unknown Job',
              company: jobData?.company || applicationData.company || 'Unknown Company',
              jobDetails: jobData || {},
            };
          })
        );
        applicationsList.sort((a, b) => {
          const getTime = (app) =>
            app.createdAt?.toDate
              ? app.createdAt.toDate().getTime()
              : app.createdAt
                ? new Date(app.createdAt).getTime()
                : app.appliedAt?.toDate
                  ? app.appliedAt.toDate().getTime()
                  : app.appliedAt
                    ? new Date(app.appliedAt).getTime()
                    : 0;
          return getTime(b) - getTime(a);
        });
        setApplications(applicationsList);
        setAppliedJobIds(applicationsList.map(app => app.jobId));

        // Fetch saved jobs
        const savedJobsQuery = query(
          collection(db, 'savedJobs'),
          where('employeeId', '==', user.uid)
        );
        const savedJobsSnapshot = await getDocs(savedJobsQuery);
        const savedJobsList = await Promise.all(
          savedJobsSnapshot.docs.map(async (docSnap) => {
            const savedJobData = docSnap.data();
            const jobId = savedJobData.jobId || savedJobData.id;
            
            if (savedJobData.isExternal) {
              return {
                ...savedJobData,
                id: docSnap.id,
                jobId: jobId,
                jobTitle: savedJobData.title || 'Unknown Job',
                company: savedJobData.company || 'Unknown Company',
                jobDetails: {
                  ...savedJobData,
                  title: savedJobData.title,
                  description: savedJobData.description || 'External job posting',
                },
              };
            }
            
            const jobDoc = await getDoc(doc(db, 'jobs', savedJobData.jobId));
            const jobData = jobDoc.exists() ? jobDoc.data() : {};
            return {
              ...savedJobData,
              id: docSnap.id,
              jobId: savedJobData.jobId,
              jobTitle: jobData?.title || savedJobData.title || 'Unknown Job',
              company: jobData?.company || savedJobData.company || 'Unknown Company',
              jobDetails: jobData,
            };
          })
        );
        savedJobsList.sort((a, b) => {
          const aTime = a.savedAt?.toDate ? a.savedAt.toDate().getTime() : new Date(a.savedAt).getTime();
          const bTime = b.savedAt?.toDate ? b.savedAt.toDate().getTime() : new Date(b.savedAt).getTime();
          return bTime - aTime;
        });
        setSavedJobs(savedJobsList);
      } catch (error) {
        console.error('Error fetching applications:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const checkUserRole = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        const employerDoc = await getDoc(doc(db, 'employers', user.uid));
        setIsEmployer(employerDoc.exists());
      } catch (error) {
        console.error('Error checking user role:', error);
      }
    };

    checkUserRole();
    fetchApplications();

    const user = auth.currentUser;
    if (!user) return;

    // Set up a real-time listener for declined jobs
    const declinedQuery = query(
      collection(db, 'declinedJobs'),
      where('employeeId', '==', user.uid)
    );

    const unsubscribe = onSnapshot(declinedQuery, async (snapshot) => {
      const declinedList = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const declinedData = docSnap.data();
          if (declinedData.isExternal) {
            return {
              id: docSnap.id,
              ...declinedData,
              jobTitle: declinedData.title || 'Unknown Job',
              company: declinedData.company || 'Unknown Company',
              jobDetails: {
                ...declinedData,
                title: declinedData.title,
                description: declinedData.description || 'External job posting',
              },
            };
          }
          const jobDoc = await getDoc(doc(db, 'jobs', declinedData.jobId));
          const jobData = jobDoc.exists() ? jobDoc.data() : {};
          return {
            id: docSnap.id,
            ...declinedData,
            jobTitle: jobData?.title || declinedData.title || 'Unknown Job',
            company: jobData?.company || declinedData.company || 'Unknown Company',
            jobDetails: jobData,
          };
        })
      );
      declinedList.sort((a, b) => {
        const aTime = a.declinedAt?.toDate ? a.declinedAt.toDate().getTime() : 0;
        const bTime = b.declinedAt?.toDate ? b.declinedAt.toDate().getTime() : 0;
        return bTime - aTime;
      });
      setDeclinedJobs(declinedList);
    }, (error) => {
      console.error("Error fetching declined jobs in real-time:", error);
      setDeclinedJobs([]);
    });

    // Clean up the listener on component unmount
    return () => unsubscribe();

  }, [navigate]);

  // Filter applications by search query
  const filteredApplications = applications.filter(app => {
    const q = searchQuery.toLowerCase();
    return (
      app.jobTitle?.toLowerCase().includes(q) ||
      app.company?.toLowerCase().includes(q) ||
      app.status?.toLowerCase().includes(q)
    );
  });

  const filteredDeclined = declinedJobs.filter(app => {
    const q = searchQuery.toLowerCase();
    return (
      app.jobTitle?.toLowerCase().includes(q) ||
      app.company?.toLowerCase().includes(q)
    );
  });
  
  const filteredSaved = savedJobs.filter(job => {
    const q = searchQuery.toLowerCase();
    return (
      job.jobTitle?.toLowerCase().includes(q) ||
      job.company?.toLowerCase().includes(q)
    );
  });

  // Apply logic for saved jobs
  const handleApplySavedJob = async (job) => {
    const user = auth.currentUser;
    if (!user || !job) return;
    setIsApplying(true);
    try {
      // Prevent duplicate applications
      const existingAppQuery = query(
        collection(db, 'applications'),
        where('employeeId', '==', user.uid),
        where('jobId', '==', job.jobId)
      );
      const existingAppSnapshot = await getDocs(existingAppQuery);
      if (!existingAppSnapshot.empty) {
        setIsApplying(false);
        setShowAppSent(true);
        setTimeout(() => setAppSentFading(true), 1500);
        setTimeout(() => {
            setShowAppSent(false);
            setAppSentFading(false);
        }, 2000);
        return;
      }
      
      const applicationData = {
        employeeId: user.uid,
        jobId: job.jobId,
        employerId: job.employerId || null,
        jobTitle: job.jobTitle,
        company: job.company,
        appliedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        status: 'pending'
      };
      
      if (job.isExternal) {
        applicationData.isExternal = true;
        applicationData.externalUrl = job.externalUrl;
      }
      
      const applicationRef = await addDoc(collection(db, 'applications'), applicationData);

      // Add right swipe to employeeSwipes collection for mutual match logic
      await addDoc(collection(db, 'employeeSwipes'), {
        employeeId: user.uid,
        employerId: job.employerId,
        jobId: job.jobId,
        direction: 'right',
        swipedAt: new Date()
      });

      if (!job.isExternal) {
        const jobRef = doc(db, 'jobs', job.jobId);
        await updateDoc(jobRef, {
          applicants: arrayUnion(user.uid)
        });
      }

      // If applying from the Passed tab, remove from declinedJobs
      if (activeTab === 'passed') {
        const declinedJobQuery = query(
          collection(db, 'declinedJobs'),
          where('employeeId', '==', user.uid),
          where('jobId', '==', job.jobId)
        );
        const snapshot = await getDocs(declinedJobQuery);
        snapshot.forEach(doc => deleteDoc(doc.ref));
        setDeclinedJobs(prev => prev.filter(declined => declined.jobId !== job.jobId));
      }

      setIsApplying(false);
      setShowAppSent(true);
      setTimeout(() => setAppSentFading(true), 1500);
      setTimeout(() => {
          setShowAppSent(false);
          setAppSentFading(false);
      }, 2000);

      // Move from saved to applied (but do NOT remove from saved)
      setApplications(prev => [{
        id: applicationRef.id,
        ...applicationData,
        jobTitle: job.jobTitle,
        company: job.company,
        jobDetails: job.jobDetails,
        isExternal: job.isExternal,
        externalUrl: job.externalUrl,
      }, ...prev]);
      setAppliedJobIds(prev => [...prev, job.jobId]);
      
    } catch (error) {
      console.error('Error applying for saved job:', error);
      setIsApplying(false);
    }
  };

  const handleUnsaveJob = async (job) => {
    const user = auth.currentUser;
    if (!user || !job) return;
    
    try {
      const q = query(
        collection(db, 'savedJobs'),
        where('employeeId', '==', user.uid),
        where('jobId', '==', job.jobId)
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        await deleteDoc(snapshot.docs[0].ref);
      }
      setSavedJobs(prev => prev.filter(saved => saved.jobId !== job.jobId));
    } catch (error) {
      console.error('Error unsaving job:', error);
    }
  };

  const handleSaveJob = async (jobOrApplication) => {
    const user = auth.currentUser;
    if (!user || !jobOrApplication) return;
    
    const jobId = jobOrApplication.jobId;
    if (!jobId) {
      console.error('No jobId found in object:', jobOrApplication);
      return;
    }
    
    // Prevent duplicate saves (but allow saving from Passed even if it's already in declinedJobs)
    if (savedJobs.some(saved => saved.jobId === jobId)) return;
    
    try {
      const savedJobData = {
        employeeId: user.uid,
        jobId: jobId,
        employerId: jobOrApplication.employerId || null,
        jobTitle: jobOrApplication.jobTitle || 'Unknown Job',
        company: jobOrApplication.company || 'Unknown Company',
        savedAt: new Date(),
        // carry over all other properties from the job object
        ...jobOrApplication.jobDetails,
        ...jobOrApplication,
      };
      
      await addDoc(collection(db, 'savedJobs'), savedJobData);
      
      setSavedJobs(prev => [
        {
          id: Math.random().toString(36).substr(2, 9), // Temp id
          ...savedJobData,
        },
        ...prev
      ]);
      // Do NOT remove from declinedJobs or Passed tab
    } catch (error) {
      console.error('Error saving job:', error);
    }
  };
  
  const handleExternalApplyConfirm = async (didApply) => {
    setShowExternalApplyConfirm(false);
    if (didApply && pendingExternalJob) {
      const user = auth.currentUser;
      if (!user) return;
      
      const jobToApply = pendingExternalJob;

      try {
        const applicationData = {
          employeeId: user.uid,
          jobId: jobToApply.jobId,
          employerId: jobToApply.employerId || null,
          jobTitle: jobToApply.jobTitle,
          company: jobToApply.company,
          appliedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          status: 'external-applied',
          isExternal: true,
          externalUrl: jobToApply.externalUrl,
        };
        const applicationRef = await addDoc(collection(db, 'applications'), applicationData);

        // Remove from saved jobs (DO NOT REMOVE FROM SAVED ANYMORE)
        // const q = query(
        //   collection(db, 'savedJobs'),
        //   where('employeeId', '==', user.uid),
        //   where('jobId', '==', jobToApply.jobId)
        // );
        // const snapshot = await getDocs(q);
        // if (!snapshot.empty) {
        //   await deleteDoc(snapshot.docs[0].ref);
        // }
        
        // Update UI
        setAppliedJobIds(prev => [...prev, jobToApply.jobId]);
        // setSavedJobs(prev => prev.filter(job => job.jobId !== jobToApply.jobId)); // REMOVE THIS LINE
        setApplications(prev => [{
            id: applicationRef.id,
            ...applicationData,
            ...jobToApply,
        }, ...prev]);

      } catch (error) {
        console.error('Error saving external application:', error);
      }
    }
    setPendingExternalJob(null);
  };

  const handleWithdrawApplication = async (application) => {
    if (!window.confirm("Are you sure you want to withdraw this application?")) return;
    setIsWithdrawing(true);
    try {
      await deleteDoc(doc(db, 'applications', application.id));
      setApplications(prev => prev.filter(app => app.id !== application.id));
      setAppliedJobIds(prev => prev.filter(id => id !== application.jobId));
      handleCloseModal();
    } catch (error) {
      console.error('Error withdrawing application:', error);
    } finally {
      setIsWithdrawing(false);
    }
  };

  const handleShowModal = (application) => {
    setSelectedApplication(application);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setModalFading(true);
    setTimeout(() => {
        setShowModal(false);
        setSelectedApplication(null);
        setModalFading(false);
    }, 300);
  };
  
  // Helper to show the Application Sent popup reliably
  const showApplicationSentPopup = () => {
    setShowAppSent(true);
    setAppSentFading(false);
    setTimeout(() => setAppSentFading(true), 1500);
    setTimeout(() => {
      setShowAppSent(false);
      setAppSentFading(false);
    }, 2000);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="applicants-page">
      <div className="applications-tabs">
        {TABS.map(tab => (
          <button 
            key={tab.key}
            className={`applications-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'applied' && (
        <div className="applicants-content">
          {filteredApplications.length === 0 ? (
            <div className="no-applicants-message">No applied jobs.</div>
          ) : (
            <div className="applicants-card-list">
              {filteredApplications.map((application) => (
                <div
                  key={`applied-${application.id}`}
                  className="applicant-card"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleShowModal(application)}
                >
                  <div className="applicant-card-header">
                    <div className="job-title-row">
                      <h3 className="job-title-cream">{application.jobTitle}</h3>
                      <p className="company-text">{application.company}</p>
                    </div>
                    <span className={`status-label status-${application.status?.toLowerCase() || 'pending'}`}>
                      {application.status || 'Pending'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 8, position: 'relative', zIndex: 2 }}>
                    <button 
                      className="view-application-btn"
                      onClick={e => {
                        e.stopPropagation();
                        handleShowModal(application);
                      }}
                    >
                      View Application
                    </button>
                    <button
                      className="view-application-btn"
                      style={{ 
                        background: savedJobs.some(saved => saved.jobId === application.jobId) ? '#888' : '#22223B', 
                        color: '#FAF3E0', 
                        border: '1.5px solid var(--primary-color)',
                        cursor: savedJobs.some(saved => saved.jobId === application.jobId) ? 'not-allowed' : 'pointer',
                        opacity: savedJobs.some(saved => saved.jobId === application.jobId) ? 0.6 : 1
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        if (!savedJobs.some(saved => saved.jobId === application.jobId)) {
                          handleSaveJob(application);
                        }
                      }}
                      disabled={savedJobs.some(saved => saved.jobId === application.jobId)}
                    >
                      {savedJobs.some(saved => saved.jobId === application.jobId) ? 'Saved' : 'Save'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'saved' && (
        <div className="applicants-content">
          {filteredSaved.length === 0 ? (
            <div className="no-applicants-message">No saved jobs.</div>
          ) : (
            <div className="applicants-card-list">
              {filteredSaved.map((job) => (
                <div
                  key={`saved-${job.id}`}
                  className="applicant-card"
                  style={{ cursor: 'pointer', position: 'relative' }}
                  onClick={() => handleShowModal(job)}
                >
                  <div className="applicant-card-header">
                    <div className="job-title-row">
                      <h3 className="job-title-cream">{job.jobTitle}</h3>
                      <p className="company-text">{job.company}</p>
                    </div>
                    <span className="status-label saved">
                      Saved
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 8, position: 'relative', zIndex: 2 }}>
                    <button 
                      className="view-application-btn"
                      style={{ 
                        background: appliedJobIds.includes(job.jobId) ? 'var(--text-secondary)' : 'var(--primary-color)',
                        color: 'var(--tab-active)'
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        if (job.isExternal) {
                          window.open(job.externalUrl, '_blank');
                          setPendingExternalJob(job);
                          setShowExternalApplyConfirm(true);
                        } else {
                          if (!appliedJobIds.includes(job.jobId)) handleApplySavedJob(job);
                        }
                      }}
                      disabled={appliedJobIds.includes(job.jobId) || isApplying}
                    >
                      {isApplying ? 'Applying...' : (appliedJobIds.includes(job.jobId) ? 'Applied' : 'Apply')}
                    </button>
                    <button
                      className="view-application-btn"
                      style={{ 
                        background: 'var(--text-color)', 
                        color: 'var(--tab-active)', 
                        border: '1.5px solid var(--primary-color)' 
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        handleUnsaveJob(job);
                      }}
                    >
                      Unsave
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'passed' && (
        <div className="applicants-content">
          {filteredDeclined.length === 0 ? (
            <div className="no-applicants-message">No passed jobs.</div>
          ) : (
            <div className="applicants-card-list">
              {filteredDeclined.map((job) => {
                const isSaved = savedJobs.some(saved => saved.jobId === job.jobId);
                return (
                  <div
                    key={`passed-${job.id}`}
                    className="applicant-card"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleShowModal(job)}
                  >
                    <div className="applicant-card-header">
                      <div className="job-title-row">
                        <h3 className="job-title-cream">{job.jobTitle}</h3>
                        <p className="company-text">{job.company}</p>
                      </div>
                      <span className="status-label passed">
                        Passed
                      </span>
                    </div>
                    <div className="applicant-card-body">
                      <div className="applicant-info-row">
                        <span className="info-label">Passed On:</span>
                        <span>{new Date(job.declinedAt?.toDate?.() || job.declinedAt).toLocaleDateString?.() || ''}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 10, marginTop: 8, position: 'relative', zIndex: 2 }}>
                        <button 
                          className="view-application-btn"
                          style={{ 
                            background: 'var(--primary-color)',
                            color: 'var(--tab-active)'
                          }}
                          onClick={e => {
                            e.stopPropagation();
                            if (job.isExternal) {
                              window.open(job.externalUrl, '_blank');
                              setPendingExternalJob(job);
                              setShowExternalApplyConfirm(true);
                            } else {
                              setPendingInternalJob(job);
                              setShowInternalApplyConfirm(true);
                            }
                          }}
                        >
                          Re-Apply
                        </button>
                        <button
                          className="view-application-btn"
                          style={{ 
                            background: isSaved ? 'var(--text-secondary)' : 'var(--text-color)', 
                            color: 'var(--tab-active)', 
                            border: '1.5px solid var(--primary-color)', 
                            opacity: isSaved ? 0.6 : 1, 
                            cursor: isSaved ? 'not-allowed' : 'pointer' 
                          }}
                          onClick={e => {
                            e.stopPropagation();
                            if (!isSaved) {
                              handleSaveJob({
                                ...job,
                                jobId: job.jobId || job.id,
                                jobTitle: job.jobTitle || job.title,
                                company: job.company || job.companyName,
                              });
                            }
                          }}
                          disabled={isSaved}
                        >
                          {isSaved ? 'Saved' : 'Save'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      
      {showAppSent && (
        <div className={`application-sent-popup ${appSentFading ? 'fade-out' : ''}`}>
          Application Sent!
        </div>
      )}

      {showExternalApplyConfirm && pendingExternalJob && (
        <div className="modal-overlay" onClick={() => handleExternalApplyConfirm(false)}>
          <div className="modal-content external-apply-modal" onClick={e => e.stopPropagation()}>
            <button className="close-modal-btn" onClick={() => handleExternalApplyConfirm(false)}>&times;</button>
            <div className="modal-header">
              <h3 className="modal-title" style={{ fontSize: '1.1rem', marginBottom: 8 }}>Did you apply for this position?</h3>
              <div style={{ fontWeight: 700, color: 'var(--primary-color)', fontSize: '1rem', marginBottom: 2 }}>{pendingExternalJob.jobTitle || 'Job Title'}</div>
              <div style={{ color: 'var(--text-color)', fontSize: '0.95rem', marginBottom: 8 }}>{pendingExternalJob.company || 'Company'}</div>
            </div>
            <div className="modal-body" style={{ textAlign: 'center', padding: 12 }}>
              <p style={{ fontSize: '0.98rem', marginBottom: 18, color: 'var(--text-secondary)' }}>Let us know if you completed your application on the external site.</p>
              <button className="external-apply-btn" style={{ marginRight: 12 }} onClick={() => handleExternalApplyConfirm(true)}>Yes</button>
              <button className="external-apply-btn secondary" onClick={() => handleExternalApplyConfirm(false)}>No</button>
            </div>
          </div>
        </div>
      )}

      {showInternalApplyConfirm && pendingInternalJob && (
        <div className="modal-overlay" onClick={() => setShowInternalApplyConfirm(false)}>
          <div className="modal-content external-apply-modal" onClick={e => e.stopPropagation()}>
            <button className="close-modal-btn" onClick={() => setShowInternalApplyConfirm(false)}>&times;</button>
            <div className="modal-header">
              <h3 className="modal-title" style={{ fontSize: '1.1rem', marginBottom: 8 }}>Are you sure you want to apply for this position?</h3>
              <div style={{ fontWeight: 700, color: 'var(--primary-color)', fontSize: '1rem', marginBottom: 2 }}>{pendingInternalJob.jobTitle || 'Job Title'}</div>
              <div style={{ color: 'var(--text-color)', fontSize: '0.95rem', marginBottom: 8 }}>{pendingInternalJob.company || 'Company'}</div>
            </div>
            <div className="modal-body" style={{ textAlign: 'center', padding: 12 }}>
              <button className="external-apply-btn" style={{ marginRight: 12 }} onClick={async () => {
                setShowInternalApplyConfirm(false);
                await handleApplySavedJob(pendingInternalJob);
                setDeclinedJobs(prev => prev.filter(declined => declined.jobId !== (pendingInternalJob.jobId || pendingInternalJob.id)));
                setPendingInternalJob(null);
                setTimeout(() => {
                  showApplicationSentPopup();
                }, 200);
              }}>Yes</button>
              <button className="external-apply-btn secondary" onClick={() => {
                setShowInternalApplyConfirm(false);
                setPendingInternalJob(null);
              }}>No</button>
            </div>
          </div>
        </div>
      )}

      {showModal && selectedApplication && (
        <div 
          className={`modal-overlay ${modalFading ? 'modal-fade-out' : ''}`} 
          onClick={handleCloseModal}
        >
          <div 
            className="modal-content modal-cream-bg"
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 className="modal-title-cream">
                {activeTab === 'saved' ? 'Job Details' : 'Application Details'}
              </h3>
              <button className="close-modal-btn" onClick={handleCloseModal}>&times;</button>
            </div>
            <div className="modal-body">
              <div style={{marginBottom: 16}}>
                <div style={{fontWeight: 700, color: 'var(--primary-color)', marginBottom: 4, textTransform: 'uppercase'}}>Job Title</div>
                <div style={{fontSize: 18, color: 'var(--text-color)', marginBottom: 12}}>{selectedApplication.jobTitle}</div>
                
                {/* Dynamically add details based on tab */}
                {activeTab === 'applied' && (
                    <>
                        {/* Application specific details can go here */}
                    </>
                )}
                
                {selectedApplication.isExternal ? (
                  <>
                    <div style={{marginBottom: 16}}>
                      <div style={{fontWeight: 700, color: 'var(--primary-color)', marginBottom: 4, textTransform: 'uppercase'}}>Company</div>
                      <div style={{fontSize: 18, color: 'var(--text-color)', marginBottom: 12}}>{selectedApplication.company}</div>
                    </div>
                    <div style={{marginBottom: 16}}>
                      <div style={{fontWeight: 700, color: 'var(--primary-color)', marginBottom: 4, textTransform: 'uppercase'}}>Description</div>
                      <div style={{fontSize: 16, color: 'var(--text-color)', marginBottom: 12, lineHeight: 1.5}}>{selectedApplication.description || 'External job posting'}</div>
                    </div>
                    <div style={{marginBottom: 24}}>
                      <div style={{fontWeight: 700, color: 'var(--primary-color)', marginBottom: 4, textTransform: 'uppercase'}}>Requirements</div>
                      <div style={{fontSize: 16, color: 'var(--text-color)', marginBottom: 12, lineHeight: 1.5}}>{selectedApplication.requirements || 'See external listing for requirements'}</div>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{marginBottom: 16}}>
                      <div style={{fontWeight: 700, color: 'var(--primary-color)', marginBottom: 4, textTransform: 'uppercase'}}>Company</div>
                      <div style={{fontSize: 18, color: 'var(--text-color)', marginBottom: 12}}>{selectedApplication.company}</div>
                    </div>
                    <div style={{marginBottom: 16}}>
                      <div style={{fontWeight: 700, color: 'var(--primary-color)', marginBottom: 4, textTransform: 'uppercase'}}>Description</div>
                      <div style={{fontSize: 16, color: 'var(--text-color)', marginBottom: 12}}>{selectedApplication.jobDetails?.description || 'No description available.'}</div>
                    </div>
                    <div style={{marginBottom: 16}}>
                      <div style={{fontWeight: 700, color: 'var(--primary-color)', marginBottom: 4, textTransform: 'uppercase'}}>Requirements</div>
                      <div style={{fontSize: 16, color: 'var(--text-color)', marginBottom: 12}}>{selectedApplication.jobDetails?.requirements || 'No requirements listed.'}</div>
                    </div>
                  </>
                )}
              </div>
              
              {activeTab === 'applied' &&
                (selectedApplication.isExternal ? (
                  <button className="view-details-btn" onClick={() => window.open(selectedApplication.externalUrl, '_blank')}>
                    View Full Details
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M15 3h6v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M10 14L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                ) : (
                  <button onClick={() => handleWithdrawApplication(selectedApplication)}>
                    Withdraw Application
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActiveApplications; 